import React, { useEffect, useMemo, useState, useRef } from 'react'
import { Box, Typography, Button, Chip, Tooltip } from '@mui/material'
import { useSelector } from 'react-redux'
import { useGetWeeklySlots } from '../globalFunctions'
import { formatTimeRangeIST, getDayLabelFromBackend, getTodayBackendDay, isSlotPastCutoff } from '../../utils/deliveryHelpers'
import getBrand from '../../brands/getBrand'

export const Delivery_timeslots = ({ deliveryType = 'pickup', setDeliveryType, selectedSlot, setSelectedSlot, selectedDay, setSelectedDay }) => {
  // Determine slot type based on delivery type
  const slotType = deliveryType === 'pickup' ? 'pickup' : 'delivery'
  const getSlots = useGetWeeklySlots(slotType)
  const brand = getBrand()
  const weeklySlots = useSelector(state => state.delivery.weeklySlots)
  
  // If setDeliveryType is not provided, use local state (for backward compatibility)
  const [localDeliveryType, setLocalDeliveryType] = useState('pickup')
  const effectiveDeliveryType = deliveryType !== undefined ? deliveryType : localDeliveryType
  const effectiveSetDeliveryType = setDeliveryType || setLocalDeliveryType

  // Use props if provided, otherwise use local state (for backward compatibility)
  const [localSelectedSlot, setLocalSelectedSlot] = useState(null)
  const [localSelectedDay, setLocalSelectedDay] = useState(null)
  
  const effectiveSelectedSlot = selectedSlot !== undefined ? selectedSlot : localSelectedSlot
  const effectiveSetSelectedSlot = setSelectedSlot || setLocalSelectedSlot
  const effectiveSelectedDay = selectedDay !== undefined ? selectedDay : localSelectedDay
  const effectiveSetSelectedDay = setSelectedDay || setLocalSelectedDay

  const prevDeliveryTypeRef = useRef(effectiveDeliveryType)
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState("");

  const [confirmedDay, setConfirmedDay] = useState(null)
  const [confirmedSlot, setConfirmedSlot] = useState(null)

  const formatSchedule = () => {
    if (!confirmedSlot || confirmedDay === null) return "";

    const today = new Date();
    const todayBackendDay = getTodayBackendDay();
    const diff = confirmedDay - todayBackendDay;

    const selectedDate = new Date();
    selectedDate.setDate(today.getDate() + (diff >= 0 ? diff : diff + 7));

    const day = selectedDate.getDate();
    const month = selectedDate.toLocaleString("default", { month: "short" });

    const timeRange = formatTimeRangeIST(
      confirmedSlot.start_time,
      confirmedSlot.end_time
    );

    return `${day} ${month}, ${timeRange}`;
  };

  useEffect(() => {
    getSlots()
    if (effectiveSelectedSlot) {
      effectiveSetSelectedSlot(null);
    }
  }, [effectiveDeliveryType]) // Refetch when delivery type changes

  // Clear selected slot when delivery type actually changes (skip initial mount)
    useEffect(() => {
      getSlots()

      // reset selection state
      effectiveSetSelectedSlot(null)
      effectiveSetSelectedDay(null)

      // reset confirmed state
      setConfirmedSlot(null)
      setConfirmedDay(null)

    }, [effectiveDeliveryType])

  // Group slots by day_of_week - only show today and tomorrow
const groupedSlots = useMemo(() => {
  const map = {}
  const today = getTodayBackendDay()
  const tomorrow = (today + 1) % 7

  weeklySlots?.forEach(slot => {
    // Only include slots for today or tomorrow
    if (slot.day_of_week !== today && slot.day_of_week !== tomorrow) return
    
    if (slot.future_booking_allowed) {
      if (slot.day_of_week < today) return
    } else {
      if (slot.day_of_week !== today) return
    }

    if (!map[slot.day_of_week]) {
      map[slot.day_of_week] = []
    }
    map[slot.day_of_week].push(slot)
  })

  return map
}, [weeklySlots])
  
  useEffect(() => {
    if (!groupedSlots) return

    const today = getTodayBackendDay()
    const tomorrow = (today + 1) % 7

    const hasToday = groupedSlots[today]?.length > 0
    const hasTomorrow = groupedSlots[tomorrow]?.length > 0

    // Only auto-select if nothing is selected yet
    if (effectiveSelectedDay === null) {
      if (hasToday) {
        effectiveSetSelectedDay(today)
      } else if (hasTomorrow) {
        effectiveSetSelectedDay(tomorrow)
      }
    }
  }, [groupedSlots])

const slotsForSelectedDay = useMemo(() => {
  if (!groupedSlots[effectiveSelectedDay]) return []
  
  return [...groupedSlots[effectiveSelectedDay]].sort(
    (a, b) => a.start_time.localeCompare(b.start_time)
  )
}, [groupedSlots, effectiveSelectedDay])

  // Removed auto-selection of first slot - user must manually select a slot
  
  

  console.log("slotsForSelectedDay", groupedSlots)
  return (
    <Box sx={{backgroundColor:"#fff",borderRadius:"8px",padding:'10px'}}>
      {/* Header */}
      <Typography fontWeight={600} mb={1}>
        Schedule your order
      </Typography>

                
        <Box display="flex" gap={2} mb={2}>
          <Box
            flex={1}
            p={1}
            borderRadius={2}
            border="1px solid"
            borderColor={effectiveDeliveryType === 'pickup' ? 'primary.main' : '#E0E0E0'}
            bgcolor={effectiveDeliveryType === 'pickup' ? '#FFF7E6' : '#fff'}
            onClick={() => effectiveSetDeliveryType('pickup')}
            sx={{ cursor: 'pointer' }}
          >
            <Box display="flex" alignItems="center" gap={1}>
                <Typography fontWeight={600} sx={{ fontSize: "12px" }}>
                    Self Pickup
                </Typography>
                🛍️
            </Box>
            <Typography variant="caption" color="text.secondary">
              {effectiveDeliveryType === 'pickup' &&
                confirmedSlot &&
                confirmedDay !== null
                  ? formatSchedule()
                  : "Pick up from store"}
            </Typography>
        </Box>

        

          {/* Schedule Delivery */}
          <Box
              flex={1}
              p={1}
              borderRadius={2}
              border="1px solid"
              borderColor={effectiveDeliveryType === 'schedule' ? 'primary.main' : '#E0E0E0'}
              bgcolor={effectiveDeliveryType === 'schedule' ? '#FFF7E6' : '#fff'}
              onClick={() => effectiveSetDeliveryType('schedule')}
              sx={{ cursor: 'pointer' }}
          >
              <Box display="flex" alignItems="center" gap={1}>
              <Typography fontWeight={600} sx={{fontSize:"12px"}}>Schedule Delivery</Typography>
              📅
              </Box>
              <Typography variant="caption" color="text.secondary">
                {effectiveDeliveryType === 'schedule' &&
                  confirmedSlot &&
                  confirmedDay !== null
                    ? formatSchedule()
                    : "Select a slot"}
              </Typography>
          </Box>
        </Box>
      {/* Day Tabs */}
     {(effectiveDeliveryType === 'schedule' || effectiveDeliveryType === 'pickup') && <>
     
      <Box display="flex" gap={1} mb={2}>
        {Object.keys(groupedSlots)
          .map(Number)
          .sort((a, b) => a - b)
          .map(day => {
            const today = new Date()
            const todayBackend = getTodayBackendDay()
            const diff = day - todayBackend
            const dateObj = new Date()
            dateObj.setDate(today.getDate() + (diff >= 0 ? diff : diff + 7))

            const d = dateObj.getDate()
            const m = dateObj.toLocaleString("default", { month: "short" })

            return (
              <Chip
                key={day}
                clickable
                onClick={() => {
                  effectiveSetSelectedDay(day)

                  // reset slot selection when day changes
                  effectiveSetSelectedSlot(null)
                  setConfirmedSlot(null)
                  setConfirmedDay(null)
                }}
                sx={{
                  height: 52,
                  px: 1.5,
                  backgroundColor:
                    effectiveSelectedDay === day
                      ? brand?.themeColor
                      : "#F5F7FA",
                  color:
                    effectiveSelectedDay === day
                      ? "#fff"
                      : brand?.themeColor,
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor:
                      effectiveSelectedDay === day
                        ? brand?.themeColor
                        : "#EAEFF5"
                  }
                }}
                label={
                  <Box textAlign="center">
                    <Typography fontSize="12px" fontWeight={600}>
                      {getDayLabelFromBackend(day)}
                    </Typography>
                    <Typography fontSize="11px">
                      {d} {m}
                    </Typography>
                  </Box>
                }
              />
            )
          })}
      </Box>


      <Typography variant="caption" color="text.secondary" mb={2}>
      {/* {slotsForSelectedDay?.some(slot => slot.is_holiday)
        ? 'Sorry it’s a Holiday'
            : 'Available Slots'} */}
        </Typography>

      <Box
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        gap={1.2}
      >
        {slotsForSelectedDay.map(slot => {
          // Check if slot has passed cutoff time
          const pastCutoff = isSlotPastCutoff(slot, effectiveSelectedDay)
          
          const isDisabled =
            !slot.is_active ||
            slot.is_holiday ||
            slot.booked_capacity >= slot.max_capacity ||
            pastCutoff

          const isSelected = effectiveSelectedSlot?.id === slot.id
          
          // Generate tooltip message for disabled slots
          let tooltipMessage = '';
          if (isDisabled) {
            if (pastCutoff) {
              tooltipMessage = `Booking closed. Cutoff time has passed (${slot.cutoff_minutes_before} minutes before slot start)`;
            } else if (slot.is_holiday) {
              tooltipMessage = 'This slot is marked as holiday';
            } else if (slot.booked_capacity >= slot.max_capacity) {
              tooltipMessage = 'Slot is fully booked';
            } else if (!slot.is_active) {
              tooltipMessage = 'Slot is inactive';
            }
          }

          const buttonContent = (
            <Button
              variant={isSelected ? 'contained' : 'outlined'}
              disabled={isDisabled}
              onClick={() => {
                effectiveSetSelectedSlot(slot)     // UI highlight
                setConfirmedSlot(slot)             // Confirm booking
                setConfirmedDay(effectiveSelectedDay)
              }}

              sx={{
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                background: isSelected ? brand?.themeColor : '#FFF',
                borderColor: brand?.themeColor,
                color:isSelected ?'white':brand?.themeColor

              }}
            >
              {formatTimeRangeIST(slot.start_time, slot.end_time)}
            </Button>
          )
          
          return tooltipMessage ? (
            <Tooltip key={slot.id} title={tooltipMessage} arrow>
              <span>{buttonContent}</span>
            </Tooltip>
          ) : (
            <Box key={slot.id}>{buttonContent}</Box>
          )
        })}
      </Box>

      <Box
        mt={3}
        p={1.5}
        bgcolor="#F5F7FA"
        borderRadius={2}
      >
        <Typography variant="caption">
          {effectiveDeliveryType === 'pickup' 
            ? 'Pickup scheduled for the selected slot.'
            : 'Delivery scheduled for the selected slot.'}
        </Typography>
      </Box> </>}
    </Box>
  )
}
